import * as React from 'react'
import {ReactElement} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {BreakPcOnly, DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {
    CenterPlainLinkStyledButton,
    PlainLinkStyledBackButton,
    PrimaryStyledButton
} from '../../../../styledComponents/Button'
import {Box} from '@mui/material';
import {Location} from 'react-router-dom'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import NatTokenRepo from '../../token/repository/NatTokenRepo'
import NatResidentUserRegistrationConfirm from './resident/NatResidentUserRegistrationConfirm'
import {NatResidentUserRegistrationInfo} from './NatResidentUserRegistrationApplicationInterface'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatGetToken} from '../../token/repository/NatGetToken'
import {Messages} from '../../../../Messages'
import {getQueryParameter} from '../../../common/util/Natutil'
import {
    NatPostResidentUserRegistrationRequest,
    NatPostResidentUserRegistrationResponse
} from '../repository/NatPostResidentUserRegistration'
import {NatResidentStepper} from '../../common/resident/NatResidentStepper'

interface Props {
    tokenRepo: NatTokenRepo,
    applicationRepo: NatApplicationRepo
    location: Location
}

interface State {
    condominiumCd: string,
    condominiumName: string,
    mailAddress: string,
    isLoadCompleted: boolean,
    selectedRidgeName: string,
    roomNumber: string,
    familyName: string,
    firstName: string,
    familyNameKana: string,
    firstNameKana: string,
    residenceStyle: string,
    showRidgeItem: boolean,
}

class NatResidentUserRegistrationConfirmPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    queryToken: string

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            condominiumCd: '',
            condominiumName: '',
            mailAddress: '',
            isLoadCompleted: false,
            selectedRidgeName: '',
            roomNumber: '',
            familyName: '',
            firstName: '',
            familyNameKana: '',
            firstNameKana: '',
            residenceStyle: '',
            showRidgeItem: false,
        }

        this.queryToken = getQueryParameter('token', this.props.location.search)
        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter, location} = this.props

        await eventEmitter.showLoading()
        try {
            const locationState = location?.state as any;
            if (locationState) {
                this.setState({
                    selectedRidgeName: locationState.selectedRidgeName ?? '',
                    roomNumber: locationState.roomNumber ?? '',
                    familyName: locationState.familyName ?? '',
                    firstName: locationState.firstName ?? '',
                    familyNameKana: locationState.familyNameKana ?? '',
                    firstNameKana: locationState.firstNameKana ?? '',
                    residenceStyle: locationState.residenceStyle ?? '',
                    showRidgeItem: locationState.showRidgeItem ?? false
                });
            }
            const tokenResponse: Nullable<NatGetToken> = await this.getTokenInfo()
            if (tokenResponse === null || tokenResponse.details === null) {
                this.props.navigate('/resident/error', {state: {message: Messages.ERESR0003.toString()}})
                return
            }

            const inputState = location.state ?? {};
            this.setState({
                condominiumCd: tokenResponse.details.condominiumCd,
                condominiumName: tokenResponse.details.condominiumName,
                mailAddress: tokenResponse.details?.mailAddress,
                selectedRidgeName: locationState?.selectedRidgeName,
                roomNumber: locationState?.roomNumber,
                familyName: locationState?.familyName,
                firstName: locationState?.firstName,
                familyNameKana: locationState?.familyNameKana,
                firstNameKana: locationState?.firstNameKana,
                residenceStyle: locationState?.residenceStyle,
                showRidgeItem: locationState?.showRidgeItem
            })
        } finally {
            eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getTokenInfo(): Promise<Nullable<NatGetToken>> {
        return await this.props.asyncCallFunction(
            this.props.tokenRepo.getToken.bind(this.props.tokenRepo, this.queryToken),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            })
    }

    async handleRegisterButtonClick(): Promise<void> {
        const {applicationRepo, eventEmitter, location} = this.props
        const {condominiumCd} = this.state
        const locationState: NatResidentUserRegistrationInfo = location.state

        await eventEmitter.showLoading()
        try {
            const roomNumber = locationState.roomNumber.length === 3 ?
                `0${locationState.roomNumber}` : locationState.roomNumber

            const request: NatPostResidentUserRegistrationRequest = {
                token: this.queryToken,
                ridgeCd: locationState.selectedRidgeCd,
                roomNumber: roomNumber,
                familyName: locationState.familyName,
                firstName: locationState.firstName,
                familyNameKana: locationState.familyNameKana,
                firstNameKana: locationState.firstNameKana,
                residenceStyle: locationState.residenceStyle,
                bankAccountNumberLast4Digits: locationState.bankAccountNumberLast4Digits,
                confirmed: true
            }

            const response: Nullable<NatPostResidentUserRegistrationResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyResidentUserRegistration.bind(
                    applicationRepo, condominiumCd, request),
                () => {
                    this.props.navigate(`/resident/error`)
                },
            )
            if (response && response.requestError === null) {
                this.props.navigate('/resident/noAuthenticationKey/complete')
            }
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleBackLinkOnClick(): void {
        const locationState: NatResidentUserRegistrationInfo = this.props.location.state
        this.props.navigate(
            `/resident/noAuthenticationKey/residentUserRegister?token=${this.queryToken}`,
            {state: {...this.props.location.state}}
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <BlockSpacer height={'30px'}/>
                <NatResidentStepper
                    stepList={[
                        {label: '手続き説明'},
                        {label: <>マンション・<BreakPcOnly/>メールアドレス登録</>},
                        {label: 'メール受信'},
                        {label: '申請者情報登録'},
                        {label: '申請者情報確認',isActive: true},
                        {label: '申請提出完了', isSquare: true}
                    ]}
                />
                <BlockSpacer height={'30px'}/>
                <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容確認
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentUserRegistrationConfirm
                    condominiumName={this.state.condominiumName}
                    mailAddress={this.state.mailAddress}
                    selectedRidgeName={this.state.selectedRidgeName}
                    roomNumber={this.state.roomNumber}
                    familyName={this.state.familyName}
                    firstName={this.state.firstName}
                    familyNameKana={this.state.familyNameKana}
                    firstNameKana={this.state.firstNameKana}
                    residenceStyle={this.state.residenceStyle}
                    showRidgeItem={this.state.showRidgeItem}
                />
                <BlockSpacer height={'30px'}/>
                <PrimaryStyledButton onClick={this.handleRegisterButtonClick}>
                    申請を提出する
                </PrimaryStyledButton>
                <BlockSpacer height={'20px'}/>
                <CenterPlainLinkStyledButton onClick={this.handleBackLinkOnClick}>
                    修正する
                </CenterPlainLinkStyledButton>
                <BlockSpacer height={'15px'}/>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentUserRegistrationConfirmPage)
